/**
 * Cross-tab export lock (ARCHITECTURE.md §7 concern: two tabs writing the
 * same destination folder at once).
 *
 * Two tabs both running an export against the same destination would race on
 * the same IndexedDB cursor/item records — read-modify-write sequences with
 * no coordination between them, which could advance a cursor past what one of
 * the tabs actually wrote, or a write from one masking the other's. One lock
 * name is enough for the whole app precisely because the design only ever
 * has one active destination folder at a time (choosing a different one
 * forgets the current run, see storage-panel.ts) — there is nothing to scope
 * the lock name to.
 *
 * Uses the Web Locks API rather than a hand-rolled localStorage flag: it is
 * the browser's own mechanism for exactly this, and — critically — it
 * releases itself automatically if a tab crashes or is killed, which a flag
 * in storage would not.
 */

const LOCK_NAME = "o365x-export";

export class ExportLockedError extends Error {
    constructor() {
        super("An export is already running in another tab");
        this.name = "ExportLockedError";
    }
}

/**
 * Runs `fn` while holding the export lock, throwing ExportLockedError instead
 * of queueing if another tab already holds it — queueing silently would leave
 * a second tab's user staring at a spinner with no idea they're waiting on a
 * different tab, possibly for hours.
 *
 * Falls back to running `fn` directly, unlocked, if the Web Locks API isn't
 * available at all — a missing safety net should not block the export
 * outright on an otherwise-capable, Chromium-only browser (§5).
 */
export async function runExclusive<T>(fn: () => Promise<T>): Promise<T> {
    if (!navigator.locks) {
        return fn();
    }

    const result = await navigator.locks.request(
        LOCK_NAME,
        { mode: "exclusive", ifAvailable: true },
        async (lock) => {
            if (!lock) {
                return { locked: false as const };
            }
            return { locked: true as const, value: await fn() };
        }
    );

    if (!result.locked) {
        throw new ExportLockedError();
    }
    return result.value;
}
